/**
 * AssetIntelligencePanel - Enhanced AI-powered asset intelligence display
 * Redesigned for clarity, readability, and actionable insights
 */

import { useState, useMemo } from 'react'
import clsx from 'clsx'
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Brain,
  Activity,
  Gauge,
  Clock,
  Wrench,
  Copy,
  Check,
  Zap
} from 'lucide-react'
import {
  HEALTH_THRESHOLDS,
  ANOMALY_THRESHOLDS,
  FAILURE_TIME_THRESHOLDS,
  CONFIDENCE_THRESHOLDS,
  calculateFailureRisk,
  getFailureRiskStatus,
  type FailureRiskLevel
} from '@/constants/thresholds'

// ============================================================================
// TYPES
// ============================================================================

interface FeatureContribution {
  feature: string
  contribution: number
  direction?: 'high' | 'low' | 'normal'
}

interface AssetIntelligencePanelProps {
  assetId: string
  healthScore: number
  anomalyScore: number
  anomalyCategory?: string
  topFeatures?: FeatureContribution[]
  failureTimeHours?: number
  confidence?: number
  recommendation?: string
  className?: string
}

type SeverityLevel = 'critical' | 'high' | 'medium' | 'low'

// ============================================================================
// SEVERITY CONFIGURATION
// ============================================================================

const SEVERITY_CONFIG = {
  critical: {
    label: 'URGENT',
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/40',
    badgeBg: 'bg-red-500',
    icon: AlertTriangle,
    pulse: true
  },
  high: {
    label: 'HIGH PRIORITY',
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/40',
    badgeBg: 'bg-amber-500',
    icon: AlertCircle,
    pulse: false
  },
  medium: {
    label: 'ATTENTION',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/10',
    borderColor: 'border-yellow-500/40',
    badgeBg: 'bg-yellow-500',
    icon: Activity,
    pulse: false
  },
  low: {
    label: 'STABLE',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/40',
    badgeBg: 'bg-emerald-500',
    icon: CheckCircle,
    pulse: false
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getSeverity(anomalyScore: number, healthScore?: number): SeverityLevel {
  // Consider both anomaly score AND health score for accurate severity
  // Health score is a cumulative measure - low health = critical regardless of current anomaly
  // Using shared threshold constants for consistency across modules

  // Critical: High anomaly OR very low health
  if (anomalyScore >= ANOMALY_THRESHOLDS.HIGH ||
      (healthScore !== undefined && healthScore < HEALTH_THRESHOLDS.CRITICAL)) {
    return 'critical'
  }

  // High: Elevated anomaly OR low health
  if (anomalyScore >= ANOMALY_THRESHOLDS.MODERATE ||
      (healthScore !== undefined && healthScore < HEALTH_THRESHOLDS.WARNING - 5)) { // 65
    return 'high'
  }

  // Medium: Moderate anomaly OR below-average health
  if (anomalyScore >= ANOMALY_THRESHOLDS.LOW ||
      (healthScore !== undefined && healthScore < HEALTH_THRESHOLDS.GOOD - 5)) { // 80
    return 'medium'
  }

  return 'low'
}

function getTimeDescription(hours: number): string {
  // Using shared threshold constants for consistency
  if (hours <= FAILURE_TIME_THRESHOLDS.IMMINENT) return 'within 24 hours'
  if (hours <= FAILURE_TIME_THRESHOLDS.URGENT) return 'tomorrow'
  if (hours <= FAILURE_TIME_THRESHOLDS.WARNING) return 'within 3 days'
  if (hours <= FAILURE_TIME_THRESHOLDS.MONITOR) return 'this week'
  return 'within 2 weeks'
}

function getFailureProbability(
  anomalyScore: number,
  failureTimeHours?: number,
  healthScore?: number
): FailureRiskLevel {
  // Use the shared calculateFailureRisk function for consistent risk calculation
  return calculateFailureRisk({
    anomalyScore,
    failureTimeHours,
    healthScore
  })
}

function formatCategory(category: string): string {
  return category.split('_').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ')
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/** Confidence indicator with visual gauge */
function ConfidenceGauge({ confidence }: { confidence: number }) {
  const percentage = Math.round(confidence * 100)
  const circumference = 2 * Math.PI * 28
  const offset = circumference - (confidence * circumference)

  // Using shared threshold constants for consistent confidence level coloring
  const getColor = () => {
    if (confidence >= CONFIDENCE_THRESHOLDS.HIGH) return { stroke: '#10b981', text: 'text-emerald-400' }
    if (confidence >= CONFIDENCE_THRESHOLDS.MEDIUM) return { stroke: '#22c55e', text: 'text-green-400' }
    if (confidence >= CONFIDENCE_THRESHOLDS.LOW) return { stroke: '#f59e0b', text: 'text-amber-400' }
    return { stroke: '#ef4444', text: 'text-red-400' }
  }

  const colors = getColor()

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-20 h-20">
        <svg className="w-20 h-20 -rotate-90" viewBox="0 0 64 64">
          {/* Background circle */}
          <circle
            cx="32"
            cy="32"
            r="28"
            fill="none"
            stroke="currentColor"
            strokeWidth="4"
            className="text-surface-700"
          />
          {/* Progress circle */}
          <circle
            cx="32"
            cy="32"
            r="28"
            fill="none"
            stroke={colors.stroke}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={clsx('text-xl font-bold font-mono', colors.text)}>
            {percentage}%
          </span>
        </div>
      </div>
      <span className="text-xs text-surface-400 mt-1">AI Confidence</span>
    </div>
  )
}

/** Feature contribution bar with label */
function FeatureBar({
  feature,
  contribution,
  direction,
  maxContribution
}: FeatureContribution & { maxContribution: number }) {
  const percentage = (contribution / maxContribution) * 100

  const directionConfig = {
    high: { color: 'bg-red-500', label: 'HIGH', textColor: 'text-red-400' },
    low: { color: 'bg-blue-500', label: 'LOW', textColor: 'text-blue-400' },
    normal: { color: 'bg-emerald-500', label: 'NORMAL', textColor: 'text-emerald-400' }
  }

  const config = direction ? directionConfig[direction] : directionConfig.high

  return (
    <div className="group">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <Gauge size={14} className="text-surface-500" />
          <span className="text-sm font-medium text-surface-200">{feature}</span>
        </div>
        <div className="flex items-center gap-3">
          {direction && (
            <span className={clsx('text-xs font-medium', config.textColor)}>
              {config.label}
            </span>
          )}
          <span className="text-sm font-mono font-bold text-surface-100 min-w-[3rem] text-right">
            {contribution.toFixed(1)}%
          </span>
        </div>
      </div>
      <div className="h-2.5 bg-surface-700 rounded-full overflow-hidden">
        <div
          className={clsx(
            'h-full rounded-full transition-all duration-500',
            config.color
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  )
}

/** Status pill for quick status indication */
function StatusPill({
  label,
  value,
  status
}: {
  label: string
  value: string
  status: 'critical' | 'warning' | 'good' | 'neutral'
}) {
  const statusColors = {
    critical: 'bg-red-500/20 text-red-400 border-red-500/30',
    warning: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    good: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    neutral: 'bg-surface-700 text-surface-300 border-surface-600'
  }

  return (
    <div className={clsx(
      'flex items-center gap-2 px-3 py-2 rounded-lg border',
      statusColors[status]
    )}>
      <span className="text-xs uppercase tracking-wider opacity-80">{label}</span>
      <span className="text-sm font-bold font-mono">{value}</span>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AssetIntelligencePanel({
  assetId,
  healthScore,
  anomalyScore,
  anomalyCategory,
  topFeatures = [],
  failureTimeHours,
  confidence = 0.89,
  recommendation,
  className
}: AssetIntelligencePanelProps) {
  const [expanded, setExpanded] = useState(true)
  const [copied, setCopied] = useState(false)

  const severity = getSeverity(anomalyScore, healthScore)
  const config = SEVERITY_CONFIG[severity]
  const SeverityIcon = config.icon

  const maxContribution = useMemo(() =>
    Math.max(...topFeatures.map(f => f.contribution), 1),
    [topFeatures]
  )

  // Generate headline based on severity
  const headline = useMemo(() => {
    if (severity === 'critical') {
      // Critical can be due to: high anomaly, low health, or both
      if (failureTimeHours) {
        return `${assetId} predicted to reach critical state ${getTimeDescription(failureTimeHours)}`
      }
      // Health-based critical (low health score without immediate failure prediction)
      if (healthScore !== undefined && healthScore < HEALTH_THRESHOLDS.CRITICAL) {
        return `${assetId} health critically low - immediate attention required`
      }
      return `${assetId} in critical condition - urgent maintenance needed`
    }
    if (severity === 'high') {
      return `${assetId} shows elevated anomaly patterns requiring attention`
    }
    if (severity === 'medium') {
      return `${assetId} has minor deviations - monitoring recommended`
    }
    return `${assetId} is operating within normal parameters`
  }, [assetId, severity, failureTimeHours, healthScore])

  // Default recommendation based on severity
  const defaultRecommendation = useMemo(() => {
    if (severity === 'critical') {
      return 'Schedule immediate inspection or take asset offline for preventive maintenance to avoid unplanned downtime.'
    }
    if (severity === 'high') {
      return 'Plan maintenance within the next 48 hours. Continue monitoring and prepare replacement parts.'
    }
    if (severity === 'medium') {
      return 'Schedule routine inspection during next maintenance window. No immediate action required.'
    }
    return 'Continue normal operations. Next scheduled maintenance can proceed as planned.'
  }, [severity])

  const handleCopy = async () => {
    const text = `Asset: ${assetId}\nHealth Score: ${healthScore}%\nAnomaly Score: ${(anomalyScore * 100).toFixed(1)}%\n${anomalyCategory ? `Pattern: ${formatCategory(anomalyCategory)}\n` : ''}${failureTimeHours ? `Time to Failure: ${failureTimeHours}h\n` : ''}\nRecommendation: ${recommendation || defaultRecommendation}`
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className={clsx(
      'rounded-xl border-2 overflow-hidden transition-all',
      config.borderColor,
      config.bgColor,
      className
    )}>
      {/* Header Section */}
      <div className="p-5">
        <div className="flex items-start gap-4">
          {/* Severity indicator and headline */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className={clsx(
                'flex items-center gap-2 px-3 py-1.5 rounded-full',
                config.badgeBg,
                config.pulse && 'animate-pulse'
              )}>
                <SeverityIcon size={14} className="text-white" />
                <span className="text-xs font-bold text-white tracking-wider">
                  {config.label}
                </span>
              </div>
              <span className="text-xs text-surface-400 font-mono">{assetId}</span>
            </div>

            <h3 className={clsx(
              'text-lg font-semibold leading-snug',
              config.color
            )}>
              {headline}
            </h3>
          </div>

          {/* Confidence gauge */}
          <ConfidenceGauge confidence={confidence} />
        </div>

        {/* Quick stats row */}
        <div className="flex flex-wrap gap-3 mt-5">
          <StatusPill
            label="Anomaly"
            value={`${(anomalyScore * 100).toFixed(1)}%`}
            status={severity === 'critical' ? 'critical' : severity === 'high' ? 'warning' : 'neutral'}
          />
          {anomalyCategory && (
            <StatusPill
              label="Pattern"
              value={formatCategory(anomalyCategory)}
              status="warning"
            />
          )}
          <StatusPill
            label="Failure Risk"
            value={getFailureProbability(anomalyScore, failureTimeHours, healthScore)}
            status={getFailureRiskStatus(getFailureProbability(anomalyScore, failureTimeHours, healthScore))}
          />
          {failureTimeHours && (
            <StatusPill
              label="Time to Failure"
              value={failureTimeHours <= FAILURE_TIME_THRESHOLDS.URGENT ? `${failureTimeHours}h` : `${Math.round(failureTimeHours / 24)}d`}
              status={failureTimeHours <= FAILURE_TIME_THRESHOLDS.IMMINENT ? 'critical' :
                      failureTimeHours <= FAILURE_TIME_THRESHOLDS.WARNING ? 'warning' : 'neutral'}
            />
          )}
        </div>
      </div>

      {/* Expandable details section */}
      <div className="border-t border-surface-700/50">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between px-5 py-3 hover:bg-surface-800/50 transition-colors"
        >
          <span className="text-sm font-medium text-surface-300">
            Key Indicators & Recommendations
          </span>
          {expanded ? (
            <ChevronUp size={18} className="text-surface-400" />
          ) : (
            <ChevronDown size={18} className="text-surface-400" />
          )}
        </button>

        {expanded && (
          <div className="px-5 pb-5 space-y-6">
            {/* Key Indicators */}
            {topFeatures.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Activity size={16} className="text-primary-400" />
                  <h4 className="text-sm font-semibold text-surface-200 uppercase tracking-wider">
                    Key Indicators
                  </h4>
                </div>
                <div className="space-y-4">
                  {topFeatures.slice(0, 5).map((feature, index) => (
                    <FeatureBar
                      key={index}
                      feature={feature.feature}
                      contribution={feature.contribution}
                      direction={feature.direction}
                      maxContribution={maxContribution}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Recommendation */}
            <div className="bg-surface-800/60 rounded-lg p-4 border border-surface-700">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary-500/20 rounded-lg">
                  <Wrench size={18} className="text-primary-400" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-surface-200 mb-2">
                    Recommended Action
                  </h4>
                  <p className="text-base text-surface-300 leading-relaxed">
                    {recommendation || defaultRecommendation}
                  </p>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 px-4 py-2 bg-surface-700 hover:bg-surface-600 text-surface-200 rounded-lg transition-colors text-sm"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? 'Copied!' : 'Copy Summary'}
              </button>
              {(severity === 'critical' || severity === 'high') && (
                <button className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors text-sm font-medium">
                  <Zap size={16} />
                  Schedule Maintenance
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer with metadata */}
      <div className="px-5 py-3 bg-surface-900/50 border-t border-surface-700/50 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-surface-500">
          <Brain size={14} />
          <span>AI Analysis • Per-Asset Model</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-surface-500">
          <Clock size={14} />
          <span>Updated just now</span>
        </div>
      </div>
    </div>
  )
}
